import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { 
  Check, 
  X, 
  ArrowRight, 
  ChevronDown, 
  ChevronUp,
  Shield,
  Eye,
  Zap,
  HeartHandshake,
  BadgePercent,
  Clock,
  TrendingDown
} from 'lucide-react';
import { useState } from 'react';

const iconMap = [Eye, BadgePercent, Shield, Clock, Zap, HeartHandshake];

export const Pricing = () => {
  const { t, translations } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const comparisonResults = [true, true, true, true, true, true, true, true];
  const traditionalResults = [false, false, false, false, false, true, true, false];

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="pricing-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden" data-testid="pricing-hero">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-emerald-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-display" data-testid="pricing-title">
            {t('pricing.hero.title')}
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-10">
            {t('pricing.hero.subtitle')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50"
            data-testid="pricing-hero-cta"
          >
            {t('pricing.getQuote')}
            <ArrowRight className="w-5 h-5" />
          </Link>

          {/* Quote Document Visual */}
          <div className="relative max-w-lg mx-auto mt-16">
            {/* Main Quote Card */}
            <div className="relative bg-gradient-to-b from-zinc-800/90 to-zinc-900/90 backdrop-blur-xl rounded-2xl border border-white/10 p-6 sm:p-8 shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_sepa-merchants-demo/artifacts/p11t5rmo_F7FB5692-8EC3-47F3-9917-09F08B4B4E2E%202.PNG" 
                    alt="CentrixPay Logo"
                    className="object-contain"
                    style={{ width: '104px', height: '96px' }}
                  />
                  <div className="text-left">
                    <div className="text-white font-semibold text-lg">CentrixPay</div>
                    <div className="text-zinc-500 text-xs">{t('pricing.quoteCard.customQuote')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-emerald-400 text-xs font-medium">{t('pricing.quoteCard.tailoredForYou')}</div>
                </div>
              </div>

              {/* Quote Lines */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-zinc-300 text-sm">{t('pricing.quoteCard.transparentPricing')}</span>
                  </div>
                  <span className="text-emerald-400 text-sm font-medium">{t('pricing.quoteCard.included')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-zinc-300 text-sm">{t('pricing.quoteCard.nextDaySettlements')}</span>
                  </div>
                  <span className="text-emerald-400 text-sm font-medium">{t('pricing.quoteCard.included')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-zinc-300 text-sm">{t('pricing.quoteCard.dedicatedSupport')}</span>
                  </div>
                  <span className="text-emerald-400 text-sm font-medium">{t('pricing.quoteCard.included')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-zinc-700/50 rounded-lg flex items-center justify-center">
                      <X className="w-4 h-4 text-zinc-500" />
                    </div>
                    <span className="text-zinc-500 text-sm line-through">{t('pricing.quoteCard.hiddenFees')}</span>
                  </div>
                  <span className="text-zinc-500 text-sm">€0.00</span>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-dashed border-white/10 my-4"></div>

              {/* Total */}
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">{t('pricing.quoteCard.yourRate')}</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-white">{t('pricing.quoteCard.competitive')}</span>
                  <div className="text-emerald-400 text-xs">{t('pricing.quoteCard.basedOnVolume')}</div>
                </div>
              </div>

              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rotate-45 translate-x-12 -translate-y-12"></div>
              </div>
            </div>

            {/* Floating savings indicator */}
            <div className="absolute -top-4 -right-4 sm:-right-8 animate-bounce" style={{ animationDuration: '3s' }}>
              <div className="bg-emerald-500 text-black font-bold text-xs px-3 py-2 rounded-full shadow-lg shadow-emerald-500/30 flex items-center gap-1">
                <TrendingDown className="w-3 h-3" />
                {t('pricing.quoteCard.lowerFees')}
              </div>
            </div>

            {/* Floating badge left */}
            <div className="absolute -left-4 sm:-left-12 top-1/2 -translate-y-1/2 hidden sm:block">
              <div className="bg-zinc-800/80 backdrop-blur-sm border border-white/10 rounded-xl p-3 shadow-lg">
                <Shield className="w-6 h-6 text-emerald-400" />
              </div>
            </div>

            {/* Floating badge right */}
            <div className="absolute -right-4 sm:-right-12 top-1/3 hidden sm:block">
              <div className="bg-zinc-800/80 backdrop-blur-sm border border-white/10 rounded-xl p-3 shadow-lg">
                <Zap className="w-6 h-6 text-amber-400" />
              </div>
            </div>

            {/* Background glow */}
            <div className="absolute -inset-4 bg-emerald-500/5 rounded-3xl blur-2xl -z-10"></div>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-16 lg:py-24" data-testid="value-props">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display">
              {t('pricing.valueProps.title')}
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              {t('pricing.valueProps.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {translations.pricing.valueProps.items.map((prop, index) => {
              const IconComponent = iconMap[index] || Eye;
              return (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all duration-300"
                  data-testid={`value-prop-${index}`}
                >
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{prop.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{prop.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 lg:py-24 bg-zinc-900/50" data-testid="comparison-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display">
              {t('pricing.comparison.title')}
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              {t('pricing.comparison.subtitle')}
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 gap-4 p-4 sm:p-6 bg-white/5 border-b border-white/10">
              <div className="text-zinc-400 text-sm font-medium">{t('pricing.comparison.feature')}</div>
              <div className="text-center">
                <span className="text-emerald-400 font-semibold">CentrixPay</span>
              </div>
              <div className="text-center">
                <span className="text-zinc-400 font-medium">{t('pricing.comparison.traditional')}</span>
              </div>
            </div>

            {/* Table Rows */}
            {translations.pricing.comparison.features.map((feature, index) => (
              <div 
                key={index}
                className={`grid grid-cols-3 gap-4 p-4 sm:p-6 ${index !== translations.pricing.comparison.features.length - 1 ? 'border-b border-white/5' : ''}`}
                data-testid={`comparison-row-${index}`}
              >
                <div className="text-zinc-300 text-sm">{feature}</div>
                <div className="flex justify-center">
                  {comparisonResults[index] ? (
                    <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center">
                      <X className="w-4 h-4 text-red-400" />
                    </div>
                  )}
                </div>
                <div className="flex justify-center">
                  {traditionalResults[index] ? (
                    <div className="w-6 h-6 bg-zinc-700 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-zinc-400" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 bg-red-500/10 rounded-full flex items-center justify-center">
                      <X className="w-4 h-4 text-red-400/60" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden" data-testid="pricing-cta-section">
        <div className="absolute inset-0">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-emerald-500/10 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-display">
            {t('pricing.ctaSection.title')}
          </h2>
          <p className="text-lg text-zinc-400 mb-8">
            {t('pricing.ctaSection.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/30"
              data-testid="pricing-final-cta"
            >
              {t('pricing.getQuote')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:+420123456789"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-semibold rounded-full hover:bg-white/5 transition-all"
            >
              {t('pricing.ctaSection.callUs')}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-zinc-900/50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 font-display">
            {t('pricing.faq.title')}
          </h2>

          <div className="space-y-4">
            {translations.pricing.faq.items.map((item, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  aria-expanded={openFaq === index}
                >
                  <span className="text-white font-medium pr-4">{item.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-emerald-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-zinc-400 shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-400 text-sm leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
